import { ReferentPriceType } from '@strata/constants';
import { CurrentBid } from '@strata/react-current-bid';
import { OutbidToast, SuccessToast } from '@strata/react-toast-message';
import Decimal from 'decimal.js';

import { postBid } from 'api/auctionsAPI';
import { handleError } from 'services/Error';
import { AppThunk } from 'store';
import { setBids } from 'store/liveReferrals/liveReferralsSlice';

export interface SocketBidsPayload {
    bids: {
        referral_id: string;
        supplier_npi: string;
        bid_amt: string;
        maxbid_amt?: string;
        tied: boolean;
    }[];
    bidAuctions: string[];
}

/** Determines the state of the bid based on npis and referrals. */
const getBidState = (
    bidOnReferrals: SocketBidsPayload['bidAuctions'],
    winningBid: SocketBidsPayload['bids'][0],
    currentNpi: string | undefined,
) => {
    if (!bidOnReferrals.includes(winningBid.referral_id)) {
        return CurrentBid.states.NO_BID;
    }
    return winningBid.supplier_npi === currentNpi
        ? CurrentBid.states.WINNING
        : CurrentBid.states.LOSING;
};

export const updateBids =
    (payload: SocketBidsPayload): AppThunk =>
    (dispatch, getState) => {
        const { bids, bidAuctions } = payload;
        const state = getState();
        const { selectedNpi } = state.auth;
        const { bidOnReferrals, winningBids } = state.liveReferrals;

        // deep clone the bids - the incoming list is immutable from state
        const currentWinningBids = structuredClone(winningBids);

        // Fetch all pieces of data needed for referral table
        const newBidOnReferrals = [...new Set([...bidOnReferrals, ...bidAuctions])];

        // Format winning bids data
        bids.forEach((winningBid) => {
            currentWinningBids[winningBid.referral_id] = {
                currentBidAmt: winningBid.bid_amt,
                currentBidState: getBidState(newBidOnReferrals, winningBid, selectedNpi),
                maxBid: winningBid.maxbid_amt ? Number(winningBid.maxbid_amt) : undefined,
                tied: winningBid.tied,
            };
        });

        dispatch(setBids({ winningBids: currentWinningBids, bidOnReferrals: newBidOnReferrals }));
    };

export const submitBid =
    ({
        referralId,
        payerId,
        segmentId,
        maxBid,
        referentPriceType,
    }: {
        referralId: string;
        payerId: string;
        segmentId: string;
        maxBid: number;
        referentPriceType: ReferentPriceType;
    }): AppThunk =>
    async (dispatch, getState) => {
        try {
            const result = await postBid({
                npi: getState().auth.selectedNpi!,
                referralId,
                payerId,
                segmentId,
                bidAmount: maxBid,
                referentPriceType,
            });

            // Update live referral data with result
            const { winningBids, bidOnReferrals } = getState().liveReferrals;
            dispatch(
                setBids({
                    winningBids: {
                        ...winningBids,
                        [referralId]: {
                            ...winningBids[referralId],
                            currentBidAmt: result.winning_bid,
                            currentBidState:
                                result.state === 'tied' ? CurrentBid.states.LOSING : result.state,
                            maxBid:
                                result.state === CurrentBid.states.WINNING
                                    ? Decimal.div(maxBid, 100).toNumber()
                                    : undefined,
                            tied: result.state === 'tied',
                        },
                    },
                    bidOnReferrals: bidOnReferrals.includes(referralId)
                        ? bidOnReferrals
                        : [...bidOnReferrals, referralId],
                }),
            );

            if (result.state === CurrentBid.states.WINNING) {
                SuccessToast({ message: 'You are the current high bidder.' });
            } else {
                OutbidToast({ message: 'Sorry, you were outbid.' });
            }
        } catch (err) {
            handleError('An error occurred submitting bid.', err as Error);
        }
    };
